import { fromEvent, merge, defer, of, Subject, Observable, asyncScheduler, Subscription } from 'rxjs';
import { filter, map, distinctUntilChanged, throttleTime } from 'rxjs/operators';
import servicesConfiguration from '@infomaniak/config-services';
import { translate } from '@ngneat/transloco';
import ResizeObserverPolyfill from 'resize-observer-polyfill';
import { HttpParams } from '@angular/common/http';

/**
 * Returns true if 'date' is not expired but 'date - margin' is
 *
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function doesDateExpiresSoon(date, // the date to compare
margin) {
    return !isDateExpired(date)
        && isDateExpired(date - margin);
}
/**
 * Returns true if `date` is a timestamp (in milliseconds!) which is in the past (before current time)
 *
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function isDateExpired(date) {
    return date <= Date.now();
}

/**
 * Returns the given date in the `YYYY-MM-DD` format without adjusting for timezone. That is, `2021-02-22 00:00:00 GMT+0200` will be
 * formatted to `2021-02-22` even if the actual timestamp for this date would give `2021-02-21`.
 *
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function formatDateAsRawYMD(date) {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return [year.toString(), month.toString().padStart(2, '0'), day.toString().padStart(2, '0')].join('-');
}

/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function daysToMilliseconds(days) {
    return days * 24 * 60 * 60 * 1000;
}
/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function hoursToMilliseconds(hours) {
    return hours * 60 * 60 * 1000;
}
/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function dateToUnixEpoch(date) {
    // not a valid date
    if (!(date instanceof Date) || isNaN(date.getTime())) {
        return 0;
    }
    return Math.round(date.getTime() / 1000);
}

/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function focusElement(element, { interval = 100, count = 2, signal, } = {}) {
    return new Promise((resolve, reject) => {
        const abort = () => {
            reject(new Error('Aborted'));
        };
        if (signal?.aborted) {
            abort();
        }
        else {
            element.focus();
            if (document.activeElement === element) {
                resolve();
            }
            else {
                if (count <= 0) {
                    reject(new Error('Unable to focus element'));
                }
                else {
                    const end = () => {
                        clearTimeout(timer);
                        if (signal !== void 0) {
                            signal.removeEventListener('abort', onAbort);
                        }
                    };
                    const onAbort = () => {
                        end();
                        abort();
                    };
                    const timer = setTimeout(() => {
                        end();
                        resolve(focusElement(element, {
                            interval,
                            count: count - 1,
                            signal,
                        }));
                    }, interval);
                    if (signal !== void 0) {
                        signal.addEventListener('abort', onAbort);
                    }
                }
            }
        }
    });
}

/**
 * Creates an Observable emitting a FocusEvent when a 'focusout' appends outside the element.
 *
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function focusLeaveElementObservable(element) {
    return fromEvent(element, 'focusout')
        .pipe(filter((event) => {
        return ((event.relatedTarget === null)
            || !element.contains(event.relatedTarget));
    }));
}

/**
 * Creates an Observable emitting true when the element if focused, and false when it is not.
 *
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function isFocusedElementObservable(element) {
    return merge(defer(() => of(element.contains(document.activeElement))), fromEvent(element, 'focusin')
        .pipe(map(() => true)), focusLeaveElementObservable(element)
        .pipe(map(() => false)))
        .pipe(distinctUntilChanged());
}

// https://gitlab.infomaniak.ch/infomaniak/front/config-services/-/blob/master/src/index.ts
var INFOMANIAK_SERVICE_IDS;
(function (INFOMANIAK_SERVICE_IDS) {
    INFOMANIAK_SERVICE_IDS[INFOMANIAK_SERVICE_IDS["HOSTING_WEB"] = 1] = "HOSTING_WEB";
    INFOMANIAK_SERVICE_IDS[INFOMANIAK_SERVICE_IDS["DEDICATED_SERVER"] = 2] = "DEDICATED_SERVER";
    INFOMANIAK_SERVICE_IDS[INFOMANIAK_SERVICE_IDS["RADIO"] = 3] = "RADIO";
    INFOMANIAK_SERVICE_IDS[INFOMANIAK_SERVICE_IDS["VIDEO"] = 4] = "VIDEO";
    INFOMANIAK_SERVICE_IDS[INFOMANIAK_SERVICE_IDS["TICKETS"] = 6] = "TICKETS";
    INFOMANIAK_SERVICE_IDS[INFOMANIAK_SERVICE_IDS["VOD"] = 7] = "VOD";
    INFOMANIAK_SERVICE_IDS[INFOMANIAK_SERVICE_IDS["HOUSING"] = 10] = "HOUSING";
    INFOMANIAK_SERVICE_IDS[INFOMANIAK_SERVICE_IDS["INFOMANIAK_RIGHTS"] = 13] = "INFOMANIAK_RIGHTS";
    INFOMANIAK_SERVICE_IDS[INFOMANIAK_SERVICE_IDS["DOMAIN"] = 14] = "DOMAIN";
    INFOMANIAK_SERVICE_IDS[INFOMANIAK_SERVICE_IDS["HOSTING_WEB_WEBSITE"] = 15] = "HOSTING_WEB_WEBSITE";
    INFOMANIAK_SERVICE_IDS[INFOMANIAK_SERVICE_IDS["HOSTING_WEB_MYSQL"] = 17] = "HOSTING_WEB_MYSQL";
    INFOMANIAK_SERVICE_IDS[INFOMANIAK_SERVICE_IDS["BACKUP"] = 18] = "BACKUP";
    INFOMANIAK_SERVICE_IDS[INFOMANIAK_SERVICE_IDS["RADIO_TERA"] = 21] = "RADIO_TERA";
    INFOMANIAK_SERVICE_IDS[INFOMANIAK_SERVICE_IDS["RADIO_FLUX"] = 22] = "RADIO_FLUX";
    INFOMANIAK_SERVICE_IDS[INFOMANIAK_SERVICE_IDS["HOSTING_MAIL"] = 23] = "HOSTING_MAIL";
    INFOMANIAK_SERVICE_IDS[INFOMANIAK_SERVICE_IDS["HOSTING_MAIL_MAILBOX"] = 24] = "HOSTING_MAIL_MAILBOX";
    INFOMANIAK_SERVICE_IDS[INFOMANIAK_SERVICE_IDS["CLOUD"] = 25] = "CLOUD";
    INFOMANIAK_SERVICE_IDS[INFOMANIAK_SERVICE_IDS["NAS"] = 26] = "NAS";
    INFOMANIAK_SERVICE_IDS[INFOMANIAK_SERVICE_IDS["AFFILIATION"] = 27] = "AFFILIATION";
    INFOMANIAK_SERVICE_IDS[INFOMANIAK_SERVICE_IDS["ACCREDITATION"] = 28] = "ACCREDITATION";
    INFOMANIAK_SERVICE_IDS[INFOMANIAK_SERVICE_IDS["NEWSLETTER"] = 29] = "NEWSLETTER";
    INFOMANIAK_SERVICE_IDS[INFOMANIAK_SERVICE_IDS["CLOUD_HD"] = 30] = "CLOUD_HD";
    INFOMANIAK_SERVICE_IDS[INFOMANIAK_SERVICE_IDS["INVITATION"] = 31] = "INVITATION";
    INFOMANIAK_SERVICE_IDS[INFOMANIAK_SERVICE_IDS["RENEWAL_WARRANTY"] = 32] = "RENEWAL_WARRANTY";
    INFOMANIAK_SERVICE_IDS[INFOMANIAK_SERVICE_IDS["DOMAIN_PRIVACY"] = 33] = "DOMAIN_PRIVACY";
    INFOMANIAK_SERVICE_IDS[INFOMANIAK_SERVICE_IDS["CERTIFICATE"] = 34] = "CERTIFICATE";
    INFOMANIAK_SERVICE_IDS[INFOMANIAK_SERVICE_IDS["JELASTIC"] = 35] = "JELASTIC";
    INFOMANIAK_SERVICE_IDS[INFOMANIAK_SERVICE_IDS["SWISS_BACKUP"] = 37] = "SWISS_BACKUP";
    INFOMANIAK_SERVICE_IDS[INFOMANIAK_SERVICE_IDS["DNS_ANYCAST"] = 38] = "DNS_ANYCAST";
    INFOMANIAK_SERVICE_IDS[INFOMANIAK_SERVICE_IDS["DRIVE"] = 40] = "DRIVE";
    INFOMANIAK_SERVICE_IDS[INFOMANIAK_SERVICE_IDS["CUSTOM_URL"] = 43] = "CUSTOM_URL";
    INFOMANIAK_SERVICE_IDS[INFOMANIAK_SERVICE_IDS["FREE_MAIL"] = 44] = "FREE_MAIL";
    INFOMANIAK_SERVICE_IDS[INFOMANIAK_SERVICE_IDS["INFOMANIAK_SITE"] = 48] = "INFOMANIAK_SITE";
    INFOMANIAK_SERVICE_IDS[INFOMANIAK_SERVICE_IDS["PUBLIC_CLOUD"] = 50] = "PUBLIC_CLOUD";
    INFOMANIAK_SERVICE_IDS[INFOMANIAK_SERVICE_IDS["KSUITE"] = 52] = "KSUITE";
    INFOMANIAK_SERVICE_IDS[INFOMANIAK_SERVICE_IDS["KCHAT"] = 54] = "KCHAT";
    INFOMANIAK_SERVICE_IDS[INFOMANIAK_SERVICE_IDS["SUPPORT_PREMIUM"] = 55] = "SUPPORT_PREMIUM";
    INFOMANIAK_SERVICE_IDS[INFOMANIAK_SERVICE_IDS["AI_TOOLS"] = 58] = "AI_TOOLS";
    INFOMANIAK_SERVICE_IDS[INFOMANIAK_SERVICE_IDS["MY_KSUITE"] = 60] = "MY_KSUITE";
})(INFOMANIAK_SERVICE_IDS || (INFOMANIAK_SERVICE_IDS = {}));

function getServiceConfiguration(serviceId) {
    return servicesConfiguration.config()[serviceId];
}
function getServiceConfigurationOrThrow(serviceId) {
    const config = getServiceConfiguration(serviceId);
    if (config === void 0) {
        throw new Error('Invalid serviceId');
    }
    else {
        return config;
    }
}

function getIconOfServiceIdUsingServicesConfig(serviceId) {
    switch (serviceId) {
        case INFOMANIAK_SERVICE_IDS.HOSTING_MAIL:
            return 'icon-product-hosting-mail';
        case INFOMANIAK_SERVICE_IDS.KSUITE:
            return 'custom-icon-k-suite';
        default:
            return `icon-product-${getServiceConfigurationOrThrow(serviceId).alias}`;
    }
}

function getServiceAlias(serviceId) {
    return getServiceConfigurationOrThrow(serviceId).alias;
}

function getServiceColor(serviceId) {
    return getServiceConfigurationOrThrow(serviceId).color;
}

/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function isError(value) {
    return (value instanceof Error);
}

/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function isNullish(value) {
    return (value === null)
        || (value === void 0);
}

/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function isObject(value) {
    return (typeof value === 'object')
        && (value !== null);
}

/**
 * Generates automatically the title of a "service" from a serviceId
 */
function getProductTitleFromServiceId(serviceId) {
    const config = getServiceConfigurationOrThrow(serviceId);
    switch (serviceId) {
        case INFOMANIAK_SERVICE_IDS.TICKETS:
            return translate('product_tickets');
        case INFOMANIAK_SERVICE_IDS.INVITATION:
            return translate('Invitations');
        default:
            return isNullish(config.text_all)
                ? getServiceTitleFromServiceIdGeneric(serviceId)
                : config.text_all;
    }
}
function getServiceTitleFromServiceIdGeneric(serviceId) {
    return translate(getServiceTitleTranslationKey(serviceId));
}
function getServiceTitleTranslationKey(serviceId) {
    return `product_${getServiceConfigurationOrThrow(serviceId).alias}`;
}

function getTransferURL({ groupId, }) {
    return `${window['MANAGER_URL']}/v3/${groupId}/accounts/transfers/new`;
}

let RESIZE_OBSERVER_AND_SUBSCRIPTION_CACHED;
function getResizeObserverAndSubscription() {
    if (RESIZE_OBSERVER_AND_SUBSCRIPTION_CACHED === void 0) {
        const subject = new Subject();
        const resizeObserver = new ResizeObserverPolyfill((entries) => {
            subject.next(entries);
        });
        RESIZE_OBSERVER_AND_SUBSCRIPTION_CACHED = {
            subject,
            resizeObserver,
        };
    }
    return RESIZE_OBSERVER_AND_SUBSCRIPTION_CACHED;
}
/*---*/
/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function fromResizeObserver(element, options) {
    return new Observable((subscriber) => {
        let running = true;
        const { subject, resizeObserver } = getResizeObserverAndSubscription();
        const subscription = subject.subscribe((entries) => {
            for (let i = 0, l = entries.length; i < l; i++) {
                const entry = entries[i];
                if (running && (entry.target === element)) {
                    subscriber.next(entry);
                    break;
                }
            }
        });
        resizeObserver.observe(element, options);
        return () => {
            if (running) {
                running = false;
                subscription.unsubscribe();
                resizeObserver.unobserve(element);
            }
        };
    });
}
/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function fromResizeObserverWithBorderRect(element, options) {
    return merge(fromResizeObserver(element, options), of(void 0))
        .pipe(map(() => element.getBoundingClientRect()));
}
/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function fromResizeObserverWithBorderRectDebounced(element, { debounceDuration = 200, ...options } = {}) {
    return fromResizeObserverWithBorderRect(element, options)
        .pipe(throttleTime(debounceDuration, asyncScheduler, { leading: true, trailing: true }));
}

/**
 * Regex that matches dates in the following date formats:
 * - `dd/mm/YYYY`
 * - `dd.mm.YYYY`
 * - `mm/dd/YYYY`
 * - `mm.dd.YYYY`
 *
 * @deprecated This constant has moved to `@infomaniak/ts-utilities`.
 */
const DATE_STRING_REGEX = /^\d\d(\.|\/)\d\d(\.|\/)\d\d\d\d$/;
/**
 * Regex that matches dates in the `YYYY-mm-dd`
 *
 * @deprecated This constant has moved to `@infomaniak/ts-utilities`.
 */
const DATE_YMD_DASH_REGEX = /^\d\d\d\d-\d\d-\d\d$/;

/* eslint-disable no-control-regex */
/* eslint-disable max-len */
/**
 * Regular expression that matches 99.99% emails addressses.
 * Source http://emailregex.com/
 *
 * @deprecated This constant has moved to `@infomaniak/ts-utilities`.
 */
const EMAIL_REGEX = /^(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9*](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/i;
/**
 * @deprecated This constant has moved to `@infomaniak/ts-utilities`.
 */
const EMAIL_SIMPLE_REGEX = /^(?:[a-z0-9-_+]+(?:\.[a-z0-9-_+]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/;
/**
 * Regular expression that matches the name of an email (the part before the @).
 * Source http://emailregex.com/
 *
 * @deprecated This constant has moved to `@infomaniak/ts-utilities`.
 */
const EMAIL_NAME_REGEX = /^(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")$/;
/**
 * @deprecated This constant has moved to `@infomaniak/ts-utilities`.
 */
const EMAIL_SIMPLE_NAME_REGEX = /^(?:[a-z0-9-_+]+(?:\.[a-z0-9-_+]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")$/;

/**
 * @deprecated This class has moved to `@infomaniak/ts-utilities`.
 */
class AsyncReference {
    constructor(getValue) {
        this.getValue = getValue;
    }
}
/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function ref(getValue) {
    return new AsyncReference(getValue);
}

/**
 * @deprecated This constant has moved to `@infomaniak/ts-utilities`.
 */
const STRICT_EQUALS = (a, b) => {
    return a === b;
};

/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function createListener(target, eventName, callback, options) {
    target.addEventListener(eventName, callback, options);
    return () => {
        target.removeEventListener(eventName, callback, options);
    };
}

/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function createInterval(callback, interval) {
    const timer = setInterval(callback, interval);
    return () => {
        clearInterval(timer);
    };
}
/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function createTimeout(callback, timeout) {
    const timer = setTimeout(callback, timeout);
    return () => {
        clearTimeout(timer);
    };
}

/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function downloadFileFromURL(url, name) {
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    document.body.appendChild(link); // Firefox support
    link.click();
    document.body.removeChild(link);
}

/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function readBlob(blob, type, options = {}) {
    return new Promise((resolve, reject) => {
        const clear = () => {
            reader.removeEventListener('load', onLoad);
            reader.removeEventListener('error', onError);
            reader.removeEventListener('abort', onAbort);
            if (signal) {
                signal.removeEventListener('abort', onAbortSignal);
            }
        };
        const onLoad = (event) => {
            clear();
            resolve(event.target.result);
        };
        const onError = () => {
            clear();
            reject(new Error('Failed to read file'));
        };
        const onAbort = () => {
            clear();
            reject(new Error('Aborted'));
        };
        const onAbortSignal = () => {
            reader.abort();
        };
        let signal;
        if (options.signal instanceof AbortSignal) {
            signal = options.signal;
            if (signal.aborted) {
                return reject(new Error('Failed to read file'));
            }
            signal.addEventListener('abort', onAbortSignal);
        }
        const reader = new FileReader();
        reader.addEventListener('load', onLoad);
        reader.addEventListener('error', onError);
        reader.addEventListener('abort', onAbort);
        switch (type) {
            case 'array-buffer':
                reader.readAsArrayBuffer(blob);
                break;
            case 'binary-string':
                reader.readAsBinaryString(blob);
                break;
            case 'data-url':
                reader.readAsDataURL(blob);
                break;
            case 'text':
                reader.readAsText(blob);
                break;
            default:
                throw new TypeError(`Invalid type: '${type}'`);
        }
    });
}
/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function readBlobAsDataURL(blob, options) {
    return readBlob(blob, 'data-url', options);
}

/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function fixURLForCSRF(url) {
    return url.href.slice(url.origin.length);
}

/**
 * @deprecated This constant has moved to `@infomaniak/ts-utilities`.
 */
const BACKEND_INFINITY = 9223372036854776e3;
/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function isInfomaniakInfinity(value) {
    return value >= BACKEND_INFINITY;
}

/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function getProgressColor({ count, total, warningPercent = 0.7, warningValue = total * (1 - warningPercent), }) {
    if (count >= total) {
        return '#F44336';
    }
    else if (((count / total) >= warningPercent)
        || ((count + warningValue) >= total)) {
        return '#FF8500';
    }
    else {
        return '#3ebf4d';
    }
}

class CustomHttpParams extends HttpParams {
    constructor(custom) {
        super();
        this.custom = custom;
    }
}
class ApiConfig {
}

class InfiniteScroll {
    constructor(options) {
        this.scrollElement = options.scrollElement;
        this.onHasReachBottomCallback = options.onHasReachBottomCallback;
        this.triggerDistance = options.triggerDistance === void 0 ? 300 : options.triggerDistance;
        this.refreshRate = options.refreshRate === void 0 ? 50 : options.refreshRate;
        this._activated = false;
        this._scrollListenerNextUpdate = 0;
    }
    isActivated() {
        return this._activated;
    }
    activate() {
        if (!this._activated) {
            this._activated = true;
            this._removeScrollListener = createListener(this.scrollElement, 'scroll', () => {
                if (Date.now() >= this._scrollListenerNextUpdate) {
                    this._scrollListenerNextUpdate = Date.now() + 50; // refresh every 50ms max
                    this.update();
                }
            });
            this._removeIntervalTimer = createInterval(() => {
                this.update();
            }, this.refreshRate);
        }
        return this;
    }
    deactivate() {
        if (this._activated) {
            this._activated = false;
            this._removeScrollListener?.();
            this._removeIntervalTimer?.();
        }
        return this;
    }
    update() {
        const bottomDistance = this.scrollElement.scrollHeight -
            this.scrollElement.scrollTop -
            this.scrollElement.offsetHeight;
        if (bottomDistance <= this.triggerDistance) {
            this.onHasReachBottomCallback();
        }
        return this;
    }
}
function createBasicInfiniteScroll(dataLoaderRef, ngZone, options) {
    return new InfiniteScroll({
        scrollElement: document.body.querySelector('mat-sidenav-content'),
        triggerDistance: 200,
        onHasReachBottomCallback: () => {
            const dataLoader = dataLoaderRef.getValue();
            if (dataLoader.pageLoadingState === 'awaiting') {
                ngZone.run(() => {
                    dataLoader.loadMoreData();
                });
            }
        },
        ...options,
    });
}

/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
// eslint-disable-next-line @typescript-eslint/no-empty-function
function noop() {
}

/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function createAbortError(message = 'Aborted') {
    const error = new Error(message);
    error.name = 'AbortError';
    return error;
}
/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function isAbortError(value) {
    return typeof value === 'object'
        && 'name' in value
        && (value.name === 'AbortError');
}
/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function throwIfNotAbortError(error) {
    if (!isAbortError(error)) {
        throw error;
    }
}
/**
 * Races between an AbortSignal 'abort' event and a promise
 *
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function raceAborted(promise, signal) {
    if (signal.aborted) {
        return Promise.reject(createAbortError());
    }
    else {
        const clear = () => {
            undoOnAborted();
        };
        let undoOnAborted;
        const abortedPromise = new Promise((resolve, reject) => {
            undoOnAborted = createListener(signal, 'abort', () => {
                clear();
                reject(createAbortError());
            });
        });
        return Promise.race([
            abortedPromise,
            promise
                .finally(clear),
        ]);
    }
}
/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function raceAbortedOptional(promise, signal) {
    return ((signal === void 0) || (signal === null))
        ? promise
        : raceAborted(promise, signal);
}
/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function isSignalAbortedOptional(signal) {
    return (signal !== void 0)
        && (signal !== null)
        && signal.aborted;
}
/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function ifSignalNotAborted(callback, signal) {
    return ((...args) => {
        if (isSignalAbortedOptional(signal)) {
            throw createAbortError();
        }
        else {
            return callback(...args);
        }
    });
}

/**
 * Returns the opposite of a sort order
 *
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function getOppositeSortOrder(order) {
    switch (order) {
        case 'asc':
            return 'desc';
        case 'desc':
        case 'none':
            return 'asc';
        default:
            throw new TypeError('Invalid sort order');
    }
}
/**
 * Creates a sort function from a sort order and a 'asc' sort function
 *  => shortcut to avoid revert
 *
 *  @deprecated This function has moved to `@infomaniak/ts-utilities`. Creates a sort function from a sort order and a 'asc' sort function
 */
function generateSortFunctionForData(sortOrder, sortFunction) {
    return (sortOrder === 'asc')
        ? sortFunction
        : (a, b) => {
            return sortFunction(b, a);
        };
}
/**
 * Sorts data using a sort function
 *
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function sortData(dataArray, sortOrder, sortFunction) {
    return (sortOrder === 'none')
        ? dataArray
        : dataArray.sort(generateSortFunctionForData(sortOrder, sortFunction));
}
/** BUILD IN SORT **/
/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function simpleCompareSortFunction(a, b) {
    if (a < b) {
        return -1;
    }
    else if (a > b) {
        return 1;
    }
    else {
        return 0;
    }
}
/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function stringSortFunction(a, b) {
    return simpleCompareSortFunction(a, b);
}
/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function stringCaseInsensitiveSortFunction(a, b) {
    return stringSortFunction(a.toLowerCase(), b.toLowerCase());
}
/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function numberSortFunction(a, b) {
    return a - b;
}
/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function numberSortReverseFunction(a, b) {
    return b - a;
}
/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function convertSortOrderToSortOptionsOrNull(sortName, sortOrder) {
    return (sortOrder === 'none')
        ? null
        : {
            name: sortName,
            order: sortOrder,
        };
}

/**
 * Universal wrapper to support various kind of paginations (ex: sync or async) through one interface
 */
class Pagination {
    constructor(getPaginatedData) {
        this._getPaginatedData = getPaginatedData;
    }
    /**
     * Gets a page
     */
    getPaginatedData(page, options) {
        return this._getPaginatedData(page, options);
    }
}
/**
 * Universal wrapper to support various kind of sortable and filterable paginations through one interface
 */
class SortableAndFilterablePagination extends Pagination {
    constructor(getPaginatedData) {
        super(getPaginatedData);
    }
    getPaginatedData(page, options) {
        return super.getPaginatedData(page, options);
    }
}
/* SIMPLE PAGINATION */
/**
 * Returns a slice of 'dataArray' using a page index and 'itemsPerPage'
 */
function paginateData(dataArray, page) {
    return dataArray.slice(page.pageIndex * page.itemsPerPage, (page.pageIndex + 1) * page.itemsPerPage);
}
/**
 * Puts page's data into 'dataArray'
 */
function reflectPaginationOnData(dataArray, page, compareFunction = STRICT_EQUALS) {
    let changed = false;
    let i = 0;
    let j = page.pageIndex * page.itemsPerPage;
    const length = page.data.length;
    for (; i < length; i++, j++) {
        const data = page.data[i];
        if ((dataArray[j] === void 0)
            || !compareFunction(dataArray[j], data)) {
            changed = true;
            dataArray[j] = data;
        }
    }
    if (dataArray.length > page.itemsCount) {
        dataArray.length = page.itemsCount; // clamp dataArray if too big
        changed = true;
    }
    return changed;
}
/**
 * Pagination for static data
 */
class SimplePagination extends SortableAndFilterablePagination {
    constructor(data, sortFunction, filterFunction) {
        super((page) => {
            return new Promise((resolve) => {
                let data = ((page.filters === null) || (page.filters === void 0))
                    ? this.data.slice()
                    : this.data.filter(this.filterFunction(page.filters));
                if ((page.sort !== null) && (page.sort !== void 0)) {
                    sortData(data, page.sort.order, this.sortFunction(page.sort.name));
                }
                data = paginateData(data, page);
                resolve({
                    data,
                    ...page,
                    itemsCount: this.data.length,
                    pageCount: Math.ceil(this.data.length / page.itemsPerPage),
                });
            });
        });
        this.data = data;
        this.sortFunction = sortFunction;
        this.filterFunction = filterFunction;
    }
}
const PAGINATED_DATA_LOADER_ON_ERROR_ERRORED = (error) => {
    console.error(error);
    return 'errored';
};
/**
 * Data loader which takes a SortableAndFilterablePagination as input and loads data for you
 */
class PaginatedDataLoader {
    constructor(options) {
        this._data = [];
        this._itemsCount = null;
        this._pageLoadingState = 'awaiting';
        this._pagination = options.pagination;
        this._onDataChange = options.onDataChange;
        this._onError = options.onError ?? PAGINATED_DATA_LOADER_ON_ERROR_ERRORED;
        this._compareFunction = options.compareFunction ?? STRICT_EQUALS;
        this._itemsPerPage = options.itemsPerPage ?? 12;
        this._pageLoadingAbortController = null;
        this._sort = null;
        this._filters = null;
        this.clearData();
    }
    // INFO: dont modify directly the object
    get sort() {
        return this._sort;
    }
    // INFO: dont modify directly the object
    get filters() {
        return this._filters;
    }
    get pageLoadingState() {
        return this._pageLoadingState;
    }
    get isErrored() {
        return (this._pageLoadingState === 'errored');
    }
    get isLoading() {
        return (this._pageLoadingState === 'awaiting')
            || (this._pageLoadingState === 'loading');
    }
    get isDone() {
        return (this._pageLoadingState === 'done');
    }
    get hasNoData() {
        return this._itemsCount === 0;
    }
    get data() {
        return this._data;
    }
    get itemsCount() {
        return this._itemsCount;
    }
    /**
     * Call this function when you want to load more data into .data
     * INFO: safe to call multiple times
     */
    loadMoreData() {
        if (this._pageLoadingState === 'awaiting') {
            this.fetchData({
                pageIndex: Math.floor(this._data.length / this._itemsPerPage),
                itemsPerPage: this._itemsPerPage,
            });
        }
    }
    /**
     * Clears .data (empty array)
     */
    clearData(sort, filters) {
        this.reset(sort, filters);
        this.updateOnDataChange();
    }
    /**
     * Forces all data (.data) to be updated from the pagination
     */
    forceDataRefresh() {
        // start = 0,
        // end: number = this._data.length,
        // start = Math.max(0, Math.min(this._data.length, start)); // clamp start in [0, data.length]
        // end = Math.max(start, Math.min(this._data.length, end)); // clamp end in [start, data.length]
        if (this._pageLoadingState === 'done') {
            this._pageLoadingState = 'awaiting';
        }
        else {
            this.abortDataLoading();
        }
        return this.fetchData({
            pageIndex: 0,
            itemsPerPage: Math.max(1, this._data.length),
        });
        // if (this._data.length === 0) {
        //   return Promise.resolve();
        // } else {
        //   // TODO reset loading state to support empty data
        //   return this.fetchData({
        //     pageIndex: 0,
        //     itemsPerPage: this._data.length,
        //   });
        // }
    }
    /**
     * Forces new data into (.data) and performs a forceDataRefresh
     */
    forceData(data, sort, filters) {
        this.reset(sort, filters);
        this._data = data;
        return this.forceDataRefresh();
    }
    abortDataLoading() {
        if (this._pageLoadingState === 'loading') {
            if (this._pageLoadingAbortController) {
                this._pageLoadingAbortController.abort();
            }
            this._pageLoadingState = 'awaiting';
        }
    }
    updateSort(sort) {
        this._sort = sort;
        this.clearData();
    }
    updateFilters(filters) {
        this._filters = filters;
        this.clearData();
    }
    fetchData(page) {
        if (this._pageLoadingAbortController) {
            this._pageLoadingAbortController.abort();
        }
        this._pageLoadingState = 'loading';
        this._pageLoadingAbortController = new AbortController();
        const signal = this._pageLoadingAbortController.signal;
        return this._pagination.getPaginatedData({
            ...page,
            sort: this._sort,
            filters: this._filters,
        }, { signal })
            .then(ifSignalNotAborted((result) => {
            if (reflectPaginationOnData(this._data, result, this._compareFunction)
                || (this._itemsCount !== result.itemsCount)) {
                this._itemsCount = result.itemsCount;
                this.updateOnDataChange();
            }
            if ((result.pageIndex + 1) >= result.pageCount) { // if next page index is greater than total page count
                this._pageLoadingState = 'done';
            }
            else {
                this._pageLoadingState = 'awaiting';
            }
        }))
            .catch((error) => {
            if (isAbortError(error)) {
                this._pageLoadingState = 'awaiting';
            }
            else {
                this._pageLoadingState = this._onError(error);
            }
        });
    }
    updateOnDataChange() {
        this._onDataChange(this._data, this._itemsCount);
    }
    reset(sort, filters) {
        this.abortDataLoading();
        this._data = [];
        this._itemsCount = null;
        if (sort !== void 0) {
            this._sort = sort;
        }
        if (filters !== void 0) {
            this._filters = filters;
        }
        this._pageLoadingState = 'awaiting';
    }
}
/**
 * Appends the sort and pagination query params to the URL for the Infomaniak's pagination api
 */
function appendSortAndPaginationToURL(url, page) {
    if ((page.sort !== null) && (page.sort !== void 0)) {
        url.searchParams.set('order_by', page.sort.name);
        url.searchParams.set(`order_for[${page.sort.name}]`, page.sort.order);
    }
    url.searchParams.set('page', (page.pageIndex + 1).toString(10));
    url.searchParams.set('per_page', (page.itemsPerPage).toString(10));
    return url;
}
/**
 * Converts the JSON response of Infomaniak's pagination api to the universal Pagination Scheme
 */
function convertPaginatedJSONToPaginatedData(result, page) {
    return {
        data: result.data,
        pageIndex: (result.page - 1),
        itemsPerPage: Number(result.items_per_page),
        pageCount: result.pages,
        itemsCount: result.total,
        sort: page.sort,
        filters: page.filters,
    };
}
function generateConvertPaginatedJSONToPaginatedData(page) {
    return (result) => {
        return convertPaginatedJSONToPaginatedData(result, page);
    };
}
/**
 * Performs an http request for the Infomaniak's pagination api
 */
function doPaginatedRequest(http, url, page, { signal, } = {}) {
    return new Promise((_resolve, _reject) => {
        appendSortAndPaginationToURL(url, page);
        const subscription = new Subscription();
        const end = () => {
            subscription.unsubscribe();
        };
        const resolve = (value) => {
            end();
            _resolve(value);
        };
        const reject = (reason) => {
            end();
            _reject(reason);
        };
        if (signal) {
            const abort = () => {
                reject(createAbortError(signal.reason));
            };
            if (signal.aborted) {
                return abort();
            }
            subscription.add(fromEvent(signal, 'abort')
                .subscribe(() => {
                abort();
            }));
        }
        subscription.add(http.get(url.href, { params: new CustomHttpParams({ skipRedirect: true, fullResponse: true }) })
            .subscribe({
            next: (result) => {
                resolve(convertPaginatedJSONToPaginatedData(result, page));
            },
            error: (error) => {
                reject(error);
            }
        }));
    });
}
function createGetPaginatedDataFunction(factory, options) {
    return (page, getPaginatedDataOptions) => {
        return factory(page, {
            ...getPaginatedDataOptions,
            ...options,
        });
    };
}
function getPaginatedDataFromDataArray(data, page) {
    return {
        data: paginateData(data, page),
        ...page,
        pageCount: Math.ceil(data.length / page.itemsPerPage),
        itemsCount: data.length,
    };
}
function doFakePaginatedRequest(data, page) {
    return Promise.resolve(getPaginatedDataFromDataArray(data, page));
}

/**
 * Regex that matches `,` or `;` and any number of whitespaces before and after it.
 *
 * @deprecated This constant has moved to `@infomaniak/ts-utilities`.
 */
const usualStringListSplittersRegex = /\s*[,;]\s*/;
/**
 * Splits the given string into an array of strings. Uses `,` and `;` to split if no value is given to the `splitters` argument. This
 * function also takes care of whitespaces around the values and effectively trims all strings within the output array.
 *
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function stringListToArray(stringList, splitters = usualStringListSplittersRegex) {
    return stringList
        .trim()
        .split(splitters);
}
/**
 * Merge a list of strings into a single string. Values are separated by `,` if no value is given to `separator`.
 *
 * This is an alias of `array.join()`.
 *
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function arrayToStringList(array, separator = ',') {
    return array.join(separator);
}

/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function onWindowResize(callback, { debounce = 200, interval = 500 } = {}) {
    let debounceTimer = null;
    const onResize = () => {
        if (debounceTimer !== null) {
            clearTimeout(debounceTimer);
            debounceTimer = null;
        }
        if (debounce > 0) {
            debounceTimer = setTimeout(() => {
                debounceTimer = null;
                callback();
            }, debounce);
        }
        else {
            callback();
        }
    };
    const removeResizeListener = createListener(window, 'resize', onResize);
    const removeIntervalTimer = (interval > 0)
        ? createInterval(onResize, interval)
        : noop;
    onResize();
    return () => {
        removeResizeListener();
        removeIntervalTimer();
        if (debounceTimer !== null) {
            clearTimeout(debounceTimer);
        }
    };
}

/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function autoresizeForStatList(element, { breakpoint = 1366, } = {}) {
    return onWindowResize(() => {
        const tableBody = element.querySelector('.table-container > ik-table-basic > .table-body');
        if (tableBody !== null) {
            if (window.innerWidth < breakpoint) {
                tableBody.style.setProperty('max-height', '400px');
            }
            else {
                tableBody.style.setProperty('max-height', '0');
                const elementClientRect = element.getBoundingClientRect();
                // console.log(
                //   tableBody.getBoundingClientRect().top,
                //   elementClientRect.top,
                //   elementClientRect.height,
                // );
                const height = elementClientRect.height - (tableBody.getBoundingClientRect().top - elementClientRect.top);
                tableBody.style.setProperty('max-height', `${height}px`);
            }
        }
    });
}

/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function getErrorMessage(err) {
    const errorDatas = err?.error?.error;
    let errorMessage;
    if (errorDatas?.errors && errorDatas.errors.length > 0) {
        errorMessage = errorDatas.errors[0].code;
    }
    else {
        errorMessage = errorDatas?.code;
    }
    return errorMessage;
}

/**
 * This decorator will cache the result of the decorated method with the parameters as key.
 *
 * Caveats and usage
 * This decorator should be used on read methods only (no properties update in the methods)
 * This decorator should be used on methods having parameters ! (the parameters are used as the cache key)
 *
 * @example
 *  @Component({
 *     template: `<ul><li *ngFor="let item of list">{{computedRender(item)}}</li></ul>`
 *  })
 * export class MyComponent {
 *     list: {firstname: string, lastname: string} = [];
 *
 *     @MethodCache()
 *     computedRender(item) {
 *         return `${item.firstname} ${item.lastname}`
 *     }
 * }
 *
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function MethodCache(options) {
    const cache = new Map();
    const ttl = options?.ttl ?? 1000 * 60 * 60;
    let start = new Date().getTime();
    return function MethodCacheDescriptorSubstitution(target, key, descriptor) {
        const originalMethod = descriptor.value;
        descriptor.value = function (...args) {
            if (start > ttl) {
                cache.clear();
                start = new Date().getTime();
            }
            const cacheKey = JSON.stringify(args);
            if (!cache.has(cacheKey)) {
                const result = originalMethod.apply(this, args);
                cache.set(cacheKey, result);
                return result;
            }
            else {
                return cache.get(cacheKey);
            }
        };
        return descriptor;
    };
}

/**
 * Debug component decorator
 * This will decorate the methods of a component showing method call with payload and execution time.
 *
 * !! this is a debug decorator. Do not keep it in production !!
 *
 * @example
 *  import {ObserveMethodCall} from '@infomaniak/ngx-helpers';
 *
 *  @ObserveMethodCall()
 *  @Component({
 *     template: `<ul><li *ngFor="let item of list">{{computedRender(item)}}</li></ul>`
 *  })
 * export class MyComponent {
 *     list: {firstname: string, lastname: string}[] = [];
 *
 *     computedRender(item) {
 *         return `${item.firstname} ${item.lastname}`
 *     }
 * }
 *
 * @param blacklist string[]
 * @param timing boolean
 * @param payload boolean
 * @param iteration boolean
 *
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function ObserveMethodCall(blacklist = [], timing = true, payload = true, iteration = true) {
    return function ObserveMethodWrapper(ctor) {
        console.error(''.padEnd(100, '*'));
        console.error('HEY !');
        console.error(`You are using the debug only ObserveMethodWrapper decorator on the ${ctor.name} component`);
        console.error('do not forget to remove it before pushing !');
        console.error(''.padEnd(100, '*'));
        Object
            .entries(Object.getOwnPropertyDescriptors(ctor.prototype))
            .filter(([prop, descriptor]) => (descriptor['value'] !== undefined &&
            !blacklist.includes(prop)))
            .forEach(([prop]) => {
            const original = ctor.prototype[prop];
            const counter = new Map();
            counter.set(prop, 0);
            ctor.prototype[prop] = function (...args) {
                const groupLog = [ctor.name + '@' + prop];
                const startLogs = ['start'];
                const endLogs = ['end'];
                if (iteration) {
                    counter.set(prop, counter.get(prop) + 1);
                    groupLog.push(`(called ${counter.get(prop)}x)`);
                }
                console.group(...groupLog);
                if (payload) {
                    startLogs.push('args:', args.length > 0 ? args : 'na');
                }
                console.log(...startLogs);
                if (timing) {
                    performance.mark('start:' + prop);
                }
                const result = original.apply(this, arguments);
                if (timing) {
                    performance.mark('end:' + prop);
                    const { name, duration } = performance.measure(prop, 'start:' + prop, 'end:' + prop);
                    endLogs.push(duration.toFixed(4));
                }
                if (payload) {
                    endLogs.push('return:', result || 'na');
                }
                console.log(...endLogs);
                console.groupEnd();
                return result;
            };
        });
    };
}

/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function findDOMElement(selector, options) {
    return findDOMElements(selector, {
        ...options,
        single: true,
    })
        .then((elements) => elements[0]);
}
/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function findDOMElements(selector, { timeout = 5000, interval = 50, parentElement = document, single = false, signal, } = {}) {
    return raceAbortedOptional(new Promise((resolve, reject) => {
        const endTime = Date.now() + timeout;
        const loop = () => {
            if (!isSignalAbortedOptional(signal)) {
                if (Date.now() > endTime) {
                    reject(new Error('Timeout'));
                }
                else {
                    if (single) {
                        const element = parentElement.querySelector(selector);
                        if (element === null) {
                            setTimeout(loop, interval);
                        }
                        else {
                            resolve([element]);
                        }
                    }
                    else {
                        const elements = parentElement.querySelectorAll(selector);
                        if (elements.length === 0) {
                            setTimeout(loop, interval);
                        }
                        else {
                            resolve(Array.from(elements));
                        }
                    }
                }
            }
        };
        loop();
    }), signal);
}

/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function openNewTab(url) {
    const win = window.open(url, '_blank', 'noopener,noreferrer');
    if (win !== null) {
        win.opener = null;
        win.location = 'http://domain.com'; // fake domain on purpose
    }
}
/**
 * @deprecated This function has moved to `@infomaniak/ts-utilities`.
 */
function goToLocation(url) {
    window.location.assign(url);
}

/**
 * Generated bundle index. Do not edit.
 */

export { ApiConfig, AsyncReference, BACKEND_INFINITY, CustomHttpParams, DATE_STRING_REGEX, DATE_YMD_DASH_REGEX, EMAIL_NAME_REGEX, EMAIL_REGEX, EMAIL_SIMPLE_NAME_REGEX, EMAIL_SIMPLE_REGEX, INFOMANIAK_SERVICE_IDS, InfiniteScroll, MethodCache, ObserveMethodCall, PAGINATED_DATA_LOADER_ON_ERROR_ERRORED, PaginatedDataLoader, Pagination, STRICT_EQUALS, SimplePagination, SortableAndFilterablePagination, appendSortAndPaginationToURL, arrayToStringList, autoresizeForStatList, convertPaginatedJSONToPaginatedData, convertSortOrderToSortOptionsOrNull, createAbortError, createBasicInfiniteScroll, createGetPaginatedDataFunction, createInterval, createListener, createTimeout, dateToUnixEpoch, daysToMilliseconds, doFakePaginatedRequest, doPaginatedRequest, doesDateExpiresSoon, downloadFileFromURL, findDOMElement, findDOMElements, fixURLForCSRF, focusElement, focusLeaveElementObservable, formatDateAsRawYMD, fromResizeObserver, fromResizeObserverWithBorderRect, fromResizeObserverWithBorderRectDebounced, generateConvertPaginatedJSONToPaginatedData, generateSortFunctionForData, getErrorMessage, getIconOfServiceIdUsingServicesConfig, getOppositeSortOrder, getPaginatedDataFromDataArray, getProductTitleFromServiceId, getProgressColor, getServiceAlias, getServiceColor, getServiceConfiguration, getServiceConfigurationOrThrow, getServiceTitleFromServiceIdGeneric, getServiceTitleTranslationKey, getTransferURL, goToLocation, hoursToMilliseconds, ifSignalNotAborted, isAbortError, isDateExpired, isError, isFocusedElementObservable, isInfomaniakInfinity, isNullish, isObject, isSignalAbortedOptional, noop, numberSortFunction, numberSortReverseFunction, onWindowResize, openNewTab, paginateData, raceAborted, raceAbortedOptional, readBlob, readBlobAsDataURL, ref, reflectPaginationOnData, simpleCompareSortFunction, sortData, stringCaseInsensitiveSortFunction, stringListToArray, stringSortFunction, throwIfNotAbortError, usualStringListSplittersRegex };
